// @flow

import { COMMITTED_CAPACITY } from '@streaming-projects/user-commit-details/committed-capacity/enums';
import {
  DEFAULT_VALUE_FOR_DB_ROW_ID,
  IS_ROW_OPENED_FOR_EDITING,
  MAX_INPUT_VALUE_FOR_ANY_FIELD,
} from '@src/constants';

export const getUnusedCommittedCapacityMetricsList = (existingConfigRows) => {
  const completeMetricsList = COMMITTED_CAPACITY.COMPLETE_METRICS_LIST;
  const usedMetricNames = getUsedCommittedCapacityMetricsList(existingConfigRows);
  return completeMetricsList.filter((x) => !usedMetricNames.includes(x.name));
};

export const getUsedCommittedCapacityMetricsList = (existingConfigRows) => {
  return existingConfigRows.map((x) => x.metricName);
};

export const getMetricConfigBasedOnMetricName = (metricName) => {
  return COMMITTED_CAPACITY.COMPLETE_METRICS_LIST.find((metric) => metric.name === metricName);
};

export const getMetricLabelFromMetricName = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricName(metricName);
  return metricConfig?.label;
};

export const getMetricDimensionsFromMetricName = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricName(metricName);
  return metricConfig?.dimensions ?? [];
};

export const getMetricDimensionsLabelsFromMetricName = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricName(metricName);
  return metricConfig?.dimensionLabels ?? [];
};

export const getMinQuantityForMetricAndUserType = (metricName, isUserAdmin) => {
  const metricConfig = getMetricConfigBasedOnMetricName(metricName);
  const keyToCheck = isUserAdmin ? 'minQuantityForAdmins' : 'minQuantityForNonAdmins';
  return metricConfig?.[keyToCheck] ?? MAX_INPUT_VALUE_FOR_ANY_FIELD;
};

export const getDimensionsPath = (metricIndex, dimensionIndex) => {
  return `${COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY}[${metricIndex}]["${COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY}"][${dimensionIndex}]`;
};

export const getMetricsPath = (metricIndex) => {
  return `${COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY}[${metricIndex}]`;
};

export const getInProgressFieldName = (metricIndex, dimensionIndex) => {
  return `${getDimensionsPath(metricIndex, dimensionIndex)}.${IS_ROW_OPENED_FOR_EDITING}`;
};

export const getIfGivenRowIsANewlyAddedRow = (givenRow) => {
  return givenRow?.[COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME] === DEFAULT_VALUE_FOR_DB_ROW_ID;
};

export const getCommittedCapacityEligibilityData = (commitData) =>
  commitData?.[COMMITTED_CAPACITY.ELIGIBILITY_DATA_BACKEND_FIELD_NAME] ?? {};

export const getDimensionLevelConfigArrayFromMetricRow = (metricRow) =>
  metricRow?.[COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY] ?? [];

export const getIfGivenRowIsBeingEditedORANewOne = (row) => {
  return getIfGivenRowIsANewlyAddedRow(row) || row[IS_ROW_OPENED_FOR_EDITING] === true;
};

export const getIfAnyOtherRowBesidesTheGivenRowIsBeingEdited = (configuredMetricRows, givenRow) => {
  return configuredMetricRows.some((metricRow) => {
    return getDimensionLevelConfigArrayFromMetricRow(metricRow).find(
      (innerRow) =>
        getIfGivenRowIsBeingEditedORANewOne(innerRow) &&
        innerRow[COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME] !==
          givenRow[COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME]
    );
  });
};

export const getIfAnyRowIsBeingEditedAnyWhereInAnyMetric = (configuredMetricRows) => {
  return configuredMetricRows.some((metric) =>
    getDimensionLevelConfigArrayFromMetricRow(metric).some((row) =>
      getIfGivenRowIsBeingEditedORANewOne(row)
    )
  );
};

export const getIfAnyRowMetricNameAsNull = (configuredMetricRows) =>
  configuredMetricRows.some((metricRow) => metricRow.metricName === null);

export const getConfiguredMetricRowsFromFormikValues = (formikValues) => {
  return formikValues?.[COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY] ?? [];
};

export const getNewDimensionsRowWithDefaults = (metricName) => {
  const dimensions = getMetricDimensionsFromMetricName(metricName);
  const newRow = Object.fromEntries(dimensions.map((dimension) => [dimension, null]));
  newRow[COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME] = DEFAULT_VALUE_FOR_DB_ROW_ID;
  return newRow;
};
